/**
 * Sonic Identity Store - 2-Step Flow
 *
 * Step 1: Your identity (prompt, intended use, voice, colour, email)
 * Step 2: Checkout (the edition, creation speed, payment)
 *
 * v2 sells one product — the bundle — so there is no package to choose between
 * the details and the checkout. The print's size and finish are chosen after
 * the customer has seen their three interpretations, in the separate physical
 * flow below.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import {
  calculateSonicPrice,
  calculateEstimatedPrice,
  SONIC_FORMAT_TYPES,
  SONIC_PHYSICAL_EDITIONS,
  findSonicEdition,
  SONIC_COLOR_PRICING,
  SONIC_SIZES,
  type SonicSizeId,
  type SonicMaterialId,
  type SonicFormatTypeId,
  type SonicPhysicalEditionId,
  SONIC_ACTIVE_CATALOG_VERSION,
  SONIC_BUNDLE_V2,
  SONIC_DELIVERY_V2,
} from '@soundbyte/config';
import { requestService } from '@soundbyte/services';

// ===== TYPES =====

// Main wizard steps (2-step flow): 1 = your identity, 2 = checkout
export type SonicWizardStep = 1 | 2;

// Digital phase steps (Phase 1) — same two steps, older name kept for callers
export type SonicDigitalStep = SonicWizardStep;

const LAST_DIGITAL_STEP: SonicDigitalStep = 2;

// Physical phase steps (Phase 2) - for separate physical ordering flow
export type SonicPhysicalStep = 1 | 2 | 3;

// Art style type (internal - always defaults to 'urban-skyline' for backend compatibility)
type SonicArtStyleId = 'urban-skyline';

// ===== PACKAGE SELECTION (Step 2) =====

export interface PackageSelectionData {
  formatType: SonicFormatTypeId;
  sizeId: SonicSizeId;
  materialId: SonicMaterialId;
  withFraming: boolean;
}

// ===== STEP 1: ESSENCE CAPTURE =====

// Curated color IDs for user selection (max 3 colors)
export type CuratedColorId =
  | 'black'         // Classic Black
  | 'charcoal'      // Charcoal
  | 'navy'          // Navy Blue
  | 'royal-blue'    // Royal Blue
  | 'teal'          // Ocean Teal
  | 'forest'        // Forest Green
  | 'emerald'       // Emerald
  | 'burgundy'      // Burgundy
  | 'crimson'       // Crimson
  | 'purple'        // Royal Purple
  | 'plum'          // Deep Plum
  | 'bronze'        // Golden Bronze
  | 'burnt-orange'  // Burnt Orange
  | 'terracotta'    // Terracotta
  | 'chocolate';    // Chocolate

// Intended use options for Sonic Identity
export type SonicIntendedUse = 'personal' | 'gift' | 'corporate' | 'memorial' | 'celebration' | 'other' | '';

// Color mode for artwork
export type SonicColorMode = 'blackAndWhite' | 'colorful';

// Essence data (Step 1)
export interface DigitalEssenceData {
  prompt: string; // User's word/phrase/name
  name: string; // Optional display name
  meaning: string; // Context/aspirations (legacy, kept for backwards compatibility)
  intendedUse: SonicIntendedUse; // What is this artwork for?
  intendedUseOther: string; // Custom text if "other" is selected
  giftOccasion: string; // Gift occasion details (e.g., Birthday, Anniversary, Wedding)
  colorMode: SonicColorMode; // Black & White or Colorful
  colorPreferences: string; // User's color preferences description (when colorful)
  selectedColorHexes: string[]; // User-selected colors as hex values (max 8)
  influenceTags: string[];
  artStyle: SonicArtStyleId;
  colorPaletteId: string | null; // Legacy: Palette-based selection
  colorPaletteName: string | null; // Legacy: Name of the selected palette
  colorPalettePrice: number; // Legacy: Price modifier from the selected palette
  selectedColors: CuratedColorId[]; // Legacy: User-selected colors (max 3)
  voiceFile: File | null;
  voiceUrl: string | null;
  voiceDuration: number;
  waveformGuideBase64: string | null;
  waveformAmplitudes: number[] | null; // Normalized amplitude values (0-1) for each bar
  email: string; // Required for delivery notification
}

// Discount code state
export interface DiscountInfo {
  code: string;
  codeId: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  discountAmount: number;
  finalAmount: number;
}

// Checkout data (Step 2)
export interface DigitalCheckoutData {
  fastTrack: boolean; // +$79 for 6-12h instead of 24-48h
  termsAccepted: boolean;
  discountCode: string;
  discountInfo: DiscountInfo | null;
}

// ===== PHASE 2: PHYSICAL UPGRADE =====

export interface PhysicalConfigData {
  editionId: SonicPhysicalEditionId; // New: predefined edition
  /**
   * Which interpretation this print is of. Null means the chosen one, which is
   * what the bundle covers; naming one of the other two makes it an extra at
   * full list price plus shipping.
   */
  artworkId: string | null;
  // Legacy fields (kept for backward compatibility)
  sizeId: SonicSizeId;
  materialId: SonicMaterialId;
  withFraming: boolean;
  shippingAddress: {
    fullName: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
  };
}

export interface PhysicalCheckoutData {
  productionConfirmed: boolean; // "I confirm. Production begins immediately."
}

export interface PhysicalOrderData {
  orderId: string | null;
  status: 'pending' | 'paid' | 'in_production' | 'shipped' | 'delivered';
  trackingNumber: string | null;
}

// ===== STORE STATE =====

interface SonicState {
  // Main wizard (3-step flow)
  digitalStep: SonicDigitalStep;
  digitalEssence: DigitalEssenceData;
  packageSelection: PackageSelectionData;
  digitalCheckout: DigitalCheckoutData;

  // Phase 2: Physical (for a specific requestId - separate flow)
  physicalStep: SonicPhysicalStep;
  physicalRequestId: string | null; // Which sonic request we're ordering prints for
  physicalConfig: PhysicalConfigData;
  physicalCheckout: PhysicalCheckoutData;
  physicalOrder: PhysicalOrderData;

  // UI state
  isSubmitting: boolean;
  error: string | null;

  // Computed values
  getDigitalPrice: () => { base: number; fastTrack: number; total: number };
  getPhysicalPrice: () => ReturnType<typeof calculateSonicPrice>;
  getPackagePrice: () => { base: number; fastTrack: number; physical: number; colorPrice: number; total: number };

  // Phase 1 Navigation
  setDigitalStep: (step: SonicDigitalStep) => void;
  nextDigitalStep: () => void;
  prevDigitalStep: () => void;
  canProceedDigital: () => boolean;
  getDigitalValidationErrors: () => Record<string, string>;

  // Phase 1 - Step 1 (Essence)
  setPrompt: (prompt: string) => void;
  setName: (name: string) => void;
  setMeaning: (meaning: string) => void;
  setIntendedUse: (use: SonicIntendedUse) => void;
  setIntendedUseOther: (text: string) => void;
  setGiftOccasion: (occasion: string) => void;
  setColorMode: (mode: SonicColorMode) => void;
  setColorPreferences: (preferences: string) => void;  // User's color preferences description
  setSelectedColorHexes: (colors: string[]) => void;   // Set selected color hex values
  toggleColorHex: (hex: string) => void;               // Toggle a color hex (add/remove)
  setInfluenceTags: (tags: string[]) => void;
  setEmail: (email: string) => void;
  setColorPalette: (paletteId: string | null, colors: string[]) => void;  // Set palette + auto-fill color hexes
  setColorPaletteId: (paletteId: string | null, paletteName?: string | null, priceModifier?: number) => void;  // Legacy: Palette selection
  setSelectedColors: (colors: CuratedColorId[]) => void;   // Legacy: Individual colors
  toggleColor: (colorId: CuratedColorId) => void;          // Legacy: Toggle individual color
  setVoiceFile: (file: File | null, url?: string | null, duration?: number, waveformGuide?: string | null, waveformAmplitudes?: number[] | null) => void;

  // Step 2 (Package Selection)
  setFormatType: (formatType: SonicFormatTypeId) => void;
  setSize: (sizeId: SonicSizeId) => void;
  setMaterial: (materialId: SonicMaterialId) => void;
  setFraming: (withFraming: boolean) => void;

  // Step 3 (Checkout)
  setFastTrack: (fastTrack: boolean) => void;
  setTermsAccepted: (accepted: boolean) => void;
  setDiscountCode: (code: string) => void;
  applyDiscount: (info: DiscountInfo) => void;
  clearDiscount: () => void;

  // Phase 1 - Submit
  uploadVoice: () => Promise<string | null>;
  submitDigitalOrder: () => Promise<boolean>;

  // Phase 2 Navigation
  setPhysicalStep: (step: SonicPhysicalStep) => void;
  nextPhysicalStep: () => void;
  prevPhysicalStep: () => void;
  canProceedPhysical: () => boolean;
  getPhysicalValidationErrors: () => Record<string, string>;

  // Phase 2 - Initialize for a specific request
  /**
   * Start the print flow. `artworkId` names which interpretation to print —
   * omit it for the chosen one, which the bundle covers.
   */
  initPhysicalOrder: (requestId: string, artworkId?: string | null) => void;

  // Phase 2 - Step 1 (Config) - Physical order flow
  setPhysicalEdition: (editionId: SonicPhysicalEditionId) => void; // New: edition-based selection
  // Legacy setters (kept for backward compatibility)
  setPhysicalSize: (sizeId: SonicSizeId) => void;
  setPhysicalMaterial: (materialId: SonicMaterialId) => void;
  setPhysicalFraming: (withFraming: boolean) => void;
  setShippingField: <K extends keyof PhysicalConfigData['shippingAddress']>(
    field: K,
    value: PhysicalConfigData['shippingAddress'][K]
  ) => void;

  // Phase 2 - Step 2 (Checkout)
  setProductionConfirmed: (confirmed: boolean) => void;

  // Phase 2 - Submit
  submitPhysicalOrder: (artworkName?: string) => Promise<boolean>;

  // UI state
  setSubmitting: (submitting: boolean) => void;
  setError: (error: string | null) => void;

  // Reset
  resetDigitalWizard: () => void;
  resetPhysicalWizard: () => void;
  resetWizard: () => void; // Combined reset for logout/cleanup
}

// ===== INITIAL STATE =====

const initialDigitalEssence: DigitalEssenceData = {
  prompt: '',
  name: '',
  meaning: '',
  intendedUse: '',
  intendedUseOther: '',
  giftOccasion: '',
  colorMode: 'blackAndWhite',
  colorPreferences: '',  // User's color preferences description
  selectedColorHexes: [], // User-selected colors as hex values (max 5)
  influenceTags: [],
  artStyle: 'urban-skyline',
  colorPaletteId: null,  // Legacy: Palette-based selection
  colorPaletteName: null, // Legacy: Name of selected palette
  colorPalettePrice: 0,  // Legacy: Price modifier from selected palette
  selectedColors: [],    // Legacy: Empty defaults to black
  voiceFile: null,
  voiceUrl: null,
  voiceDuration: 0,
  waveformGuideBase64: null,
  waveformAmplitudes: null,
  email: '',
};

const initialPackageSelection: PackageSelectionData = {
  formatType: 'digital',
  sizeId: 'medium',
  materialId: 'paper',
  withFraming: false,
};

const initialDigitalCheckout: DigitalCheckoutData = {
  fastTrack: false,
  termsAccepted: false,
  discountCode: '',
  discountInfo: null,
};

// v2 opens on the edition the bundle already includes, so the customer starts
// at no extra charge and upgrades deliberately. The v1 default —
// medium-aluminium-unframed — does not exist in v2 at all, so leaving it would
// open the page on a combination the price authority refuses to sell.
const initialPhysicalConfig: PhysicalConfigData = {
  editionId: (SONIC_ACTIVE_CATALOG_VERSION >= 2
    ? SONIC_BUNDLE_V2.includedEditionId
    : 'medium-aluminium-unframed') as SonicPhysicalEditionId,
  artworkId: null,
  // Legacy fields
  sizeId: 'small' as SonicSizeId,
  materialId: (SONIC_ACTIVE_CATALOG_VERSION >= 2
    ? 'paper'
    : 'aluminium-silver') as SonicMaterialId,
  withFraming: SONIC_ACTIVE_CATALOG_VERSION >= 2,
  shippingAddress: {
    fullName: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
    country: '',
    phone: '',
  },
};

const initialPhysicalCheckout: PhysicalCheckoutData = {
  productionConfirmed: false,
};

const initialPhysicalOrder: PhysicalOrderData = {
  orderId: null,
  status: 'pending',
  trackingNumber: null,
};

// ===== PRICING CONSTANTS =====
const DIGITAL_BASE_PRICE = 99;
const FAST_TRACK_PRICE = 79;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Whether the submission has everything the studio needs: a voice recording
 * (any duration), at least one word, and an email to deliver to. Gates both
 * leaving step 1 and paying on step 2.
 */
export function isEssenceComplete(essence: DigitalEssenceData): boolean {
  const hasVoice =
    essence.voiceFile !== null || (essence.voiceUrl !== null && essence.voiceUrl.length > 0);
  const hasPrompt = essence.prompt.trim().length >= 1;
  const hasEmail = EMAIL_PATTERN.test(essence.email);
  return hasVoice && hasPrompt && hasEmail;
}

// ===== STORE =====

export const useSonicStore = create<SonicState>()(
  persist(
    (set, get) => ({
      // Initial state - Main wizard (3-step flow)
      digitalStep: 1,
      digitalEssence: { ...initialDigitalEssence },
      packageSelection: { ...initialPackageSelection },
      digitalCheckout: { ...initialDigitalCheckout },

      // Initial state - Phase 2
      physicalStep: 1,
      physicalRequestId: null,
      physicalConfig: { ...initialPhysicalConfig },
      physicalCheckout: { ...initialPhysicalCheckout },
      physicalOrder: { ...initialPhysicalOrder },

      // UI state
      isSubmitting: false,
      error: null,

      // ===== COMPUTED VALUES =====

      getDigitalPrice: () => {
        const { digitalCheckout } = get();
        const base = DIGITAL_BASE_PRICE;
        const fastTrack = digitalCheckout.fastTrack ? FAST_TRACK_PRICE : 0;
        return {
          base,
          fastTrack,
          total: base + fastTrack,
        };
      },

      getPackagePrice: () => {
        const { packageSelection, digitalCheckout, digitalEssence } = get();
        const formatType = SONIC_FORMAT_TYPES.find(f => f.id === packageSelection.formatType);
        const base = formatType?.startingPrice || 99;
        const fastTrack = (packageSelection.formatType === 'digital' || packageSelection.formatType === 'phygital') && digitalCheckout.fastTrack ? FAST_TRACK_PRICE : 0;

        // Calculate physical component price
        let physical = 0;
        if (packageSelection.formatType === 'physical' || packageSelection.formatType === 'phygital') {
          physical = calculateEstimatedPrice({
            formatTypeId: packageSelection.formatType,
            sizeId: packageSelection.sizeId,
            materialId: packageSelection.materialId,
            withFraming: packageSelection.withFraming,
          }) - base; // Subtract base since it's already included
        }

        // Color pricing: flat $149 for colorful mode, $0 for black & white
        const colorPrice = digitalEssence.colorMode === 'colorful' ? SONIC_COLOR_PRICING.colorfulPrice : 0;

        return {
          base,
          fastTrack,
          physical,
          colorPrice,
          total: base + fastTrack + physical + colorPrice,
        };
      },

      getPhysicalPrice: () => {
        const { physicalConfig } = get();
        return calculateSonicPrice({
          editionId: physicalConfig.editionId,
          // Legacy fallback
          sizeId: physicalConfig.sizeId,
          materialId: physicalConfig.materialId,
          withFraming: physicalConfig.withFraming,
          deliveryId: 'standard', // Physical always uses standard shipping
        });
      },

      // ===== MAIN WIZARD NAVIGATION (3-step flow) =====

      setDigitalStep: (step) => set({ digitalStep: step }),

      nextDigitalStep: () => {
        const { digitalStep, canProceedDigital } = get();
        if (canProceedDigital() && digitalStep < LAST_DIGITAL_STEP) {
          set({ digitalStep: (digitalStep + 1) as SonicDigitalStep, error: null });
        }
      },

      prevDigitalStep: () => {
        const { digitalStep } = get();
        if (digitalStep > 1) {
          set({ digitalStep: (digitalStep - 1) as SonicDigitalStep, error: null });
        }
      },

      // Whether the current step can be left for the next one. Only step 1
      // has a next step; paying on step 2 is gated by the checkout itself.
      canProceedDigital: () => {
        const { digitalStep, digitalEssence } = get();
        return digitalStep === 1 && isEssenceComplete(digitalEssence);
      },

      // Field-level errors for the details step, shown beside the inputs.
      getDigitalValidationErrors: () => {
        const { digitalEssence } = get();
        const errors: Record<string, string> = {};

        if (digitalEssence.voiceFile === null && (!digitalEssence.voiceUrl || digitalEssence.voiceUrl.length === 0)) {
          errors.voice = 'Voice recording is required';
        }
        if (digitalEssence.prompt.trim().length < 1) {
          errors.prompt = 'Enter at least one word or phrase';
        }
        if (!digitalEssence.email.trim()) {
          errors.email = 'Email is required for delivery notification';
        } else if (!EMAIL_PATTERN.test(digitalEssence.email)) {
          errors.email = 'Please enter a valid email';
        }

        return errors;
      },

      // ===== PHASE 1 - STEP 1 (ESSENCE) =====

      setPrompt: (prompt) =>
        set({ digitalEssence: { ...get().digitalEssence, prompt } }),

      setName: (name) =>
        set({ digitalEssence: { ...get().digitalEssence, name } }),

      setMeaning: (meaning) =>
        set({ digitalEssence: { ...get().digitalEssence, meaning } }),

      setIntendedUse: (intendedUse) =>
        set({ digitalEssence: { ...get().digitalEssence, intendedUse } }),

      setIntendedUseOther: (intendedUseOther) =>
        set({ digitalEssence: { ...get().digitalEssence, intendedUseOther } }),

      setGiftOccasion: (giftOccasion) =>
        set({ digitalEssence: { ...get().digitalEssence, giftOccasion } }),

      setColorMode: (colorMode) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            colorMode,
            // Reset color preferences and selections when switching to black and white
            colorPreferences: colorMode === 'blackAndWhite' ? '' : get().digitalEssence.colorPreferences,
            selectedColorHexes: colorMode === 'blackAndWhite' ? [] : get().digitalEssence.selectedColorHexes,
            // Legacy: Reset palette when switching to black and white
            colorPaletteId: colorMode === 'blackAndWhite' ? null : get().digitalEssence.colorPaletteId,
            colorPaletteName: colorMode === 'blackAndWhite' ? null : get().digitalEssence.colorPaletteName,
            colorPalettePrice: colorMode === 'blackAndWhite' ? 0 : get().digitalEssence.colorPalettePrice,
          },
        }),

      setColorPreferences: (colorPreferences) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            colorPreferences,
          },
        }),

      setSelectedColorHexes: (colors) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            selectedColorHexes: colors.slice(0, 8), // Max 8 colors
          },
        }),

      toggleColorHex: (hex) => {
        const { digitalEssence } = get();
        const currentColors = digitalEssence.selectedColorHexes;
        const isSelected = currentColors.includes(hex);

        let newColors: string[];
        if (isSelected) {
          // Remove color
          newColors = currentColors.filter((c) => c !== hex);
        } else {
          // Add color (max 8)
          if (currentColors.length >= 8) {
            // Replace oldest color with new one
            newColors = [...currentColors.slice(1), hex];
          } else {
            newColors = [...currentColors, hex];
          }
        }

        set({
          digitalEssence: {
            ...get().digitalEssence,
            selectedColorHexes: newColors,
            // Clear palette label when individual colors are toggled
            colorPaletteId: null,
          },
        });
      },

      setInfluenceTags: (tags) =>
        set({ digitalEssence: { ...get().digitalEssence, influenceTags: tags } }),

      setEmail: (email) =>
        set({ digitalEssence: { ...get().digitalEssence, email } }),

      setColorPalette: (paletteId, colors) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            colorPaletteId: paletteId,
            selectedColorHexes: paletteId ? colors.slice(0, 8) : [],
            colorMode: paletteId ? 'colorful' : get().digitalEssence.colorMode,
          },
        }),

      setColorPaletteId: (paletteId, paletteName = null, priceModifier = 0) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            colorPaletteId: paletteId,
            colorPaletteName: paletteId ? paletteName : null,
            colorPalettePrice: paletteId ? priceModifier : 0,
            // Clear legacy selectedColors when palette is set
            selectedColors: paletteId ? [] : get().digitalEssence.selectedColors,
          },
        }),

      setSelectedColors: (colors) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            selectedColors: colors.slice(0, 3),
            // Clear colorPaletteId when individual colors are set
            colorPaletteId: colors.length > 0 ? null : get().digitalEssence.colorPaletteId,
          },
        }),

      toggleColor: (colorId) => {
        const { digitalEssence } = get();
        const currentColors = digitalEssence.selectedColors;
        const isSelected = currentColors.includes(colorId);

        let newColors: CuratedColorId[];
        if (isSelected) {
          // Remove color
          newColors = currentColors.filter((c) => c !== colorId);
        } else {
          // Add color (max 3)
          if (currentColors.length >= 3) {
            // Replace oldest color with new one
            newColors = [...currentColors.slice(1), colorId];
          } else {
            newColors = [...currentColors, colorId];
          }
        }

        // Update state
        set({ digitalEssence: { ...get().digitalEssence, selectedColors: newColors } });
      },

      setVoiceFile: (file, url = null, duration = 0, waveformGuide = null, waveformAmplitudes = null) =>
        set({
          digitalEssence: {
            ...get().digitalEssence,
            voiceFile: file,
            voiceUrl: url,
            voiceDuration: duration,
            waveformGuideBase64: waveformGuide,
            waveformAmplitudes: waveformAmplitudes,
          },
        }),

      // ===== STEP 2 (PACKAGE SELECTION) =====

      setFormatType: (formatType) =>
        set({ packageSelection: { ...get().packageSelection, formatType } }),

      setSize: (sizeId) =>
        set({ packageSelection: { ...get().packageSelection, sizeId } }),

      setMaterial: (materialId) =>
        set({ packageSelection: { ...get().packageSelection, materialId } }),

      setFraming: (withFraming) =>
        set({ packageSelection: { ...get().packageSelection, withFraming } }),

      // ===== STEP 3 (CHECKOUT) =====

      setFastTrack: (fastTrack) =>
        set({ digitalCheckout: { ...get().digitalCheckout, fastTrack } }),

      setTermsAccepted: (accepted) =>
        set({ digitalCheckout: { ...get().digitalCheckout, termsAccepted: accepted } }),

      setDiscountCode: (code) =>
        set({ digitalCheckout: { ...get().digitalCheckout, discountCode: code } }),

      applyDiscount: (info) =>
        set({ digitalCheckout: { ...get().digitalCheckout, discountInfo: info, discountCode: info.code } }),

      clearDiscount: () =>
        set({ digitalCheckout: { ...get().digitalCheckout, discountCode: '', discountInfo: null } }),

      // ===== PHASE 1 - UPLOAD & SUBMIT =====

      uploadVoice: async () => {
        const { digitalEssence } = get();
        if (!digitalEssence.voiceFile) return digitalEssence.voiceUrl;

        set({ isSubmitting: true, error: null });

        try {
          const response = await requestService.uploadSonicVoice(digitalEssence.voiceFile);
          if (response.success && response.data) {
            set({
              digitalEssence: { ...get().digitalEssence, voiceUrl: response.data.url },
              isSubmitting: false,
            });
            return response.data.url;
          } else {
            set({ error: response.error?.message || 'Failed to upload voice', isSubmitting: false });
            return null;
          }
        } catch (err) {
          set({ error: 'Failed to upload voice file', isSubmitting: false });
          return null;
        }
      },

      submitDigitalOrder: async () => {
        const state = get();
        const { digitalEssence, packageSelection, digitalCheckout, physicalConfig, getPackagePrice } = state;
        set({ isSubmitting: true, error: null });

        try {
          // 1. Upload voice file if exists and needs upload
          let voiceUrl = digitalEssence.voiceUrl;
          const needsUpload = digitalEssence.voiceFile && (!voiceUrl || voiceUrl.startsWith('blob:'));
          if (needsUpload) {
            voiceUrl = await state.uploadVoice();
            // Check if upload failed - don't proceed without voice
            if (!voiceUrl) {
              set({
                error: 'Failed to upload voice recording. Please try again.',
                isSubmitting: false,
              });
              return false;
            }
          }

          // 2. Get pricing based on package selection
          const pricing = getPackagePrice();

          // 3. Build request data
          const requestData: any = {
            name: digitalEssence.name || digitalEssence.prompt.substring(0, 50),
            prompt: digitalEssence.prompt,
            meaning: digitalEssence.meaning || undefined, // Legacy "Story Behind" text
            intendedUse: digitalEssence.intendedUse || undefined, // What is this artwork for?
            intendedUseOther: digitalEssence.intendedUse === 'other' ? digitalEssence.intendedUseOther : undefined,
            giftOccasion: digitalEssence.intendedUse === 'gift' ? digitalEssence.giftOccasion : undefined,
            colorMode: digitalEssence.colorMode,
            colorPreferences: digitalEssence.colorMode === 'colorful' ? digitalEssence.colorPreferences : undefined,
            selectedColorHexes: digitalEssence.colorMode === 'colorful' && digitalEssence.selectedColorHexes.length > 0
              ? digitalEssence.selectedColorHexes
              : undefined,
            influenceTags: digitalEssence.influenceTags,
            artStyle: digitalEssence.artStyle,
            // Legacy: Color selection (palette-based or individual colors)
            colorPaletteId: digitalEssence.colorPaletteId || undefined,
            selectedColors: digitalEssence.selectedColors,
            voiceUrl: voiceUrl || undefined,
            voiceDuration: digitalEssence.voiceDuration || undefined,
            waveformGuideBase64: digitalEssence.waveformGuideBase64 || undefined,
            waveformAmplitudes: digitalEssence.waveformAmplitudes || undefined,
            // Package selection. Under v2 the product is the bundle: the
            // backend ignores these three, but the request schema still
            // requires them, so send the bundle's own included edition rather
            // than whatever stale value the wizard happens to hold.
            formatType: SONIC_ACTIVE_CATALOG_VERSION >= 2 ? 'digital' : packageSelection.formatType,
            packageSize: SONIC_ACTIVE_CATALOG_VERSION >= 2 ? 'small' : packageSelection.sizeId,
            packageMaterial: SONIC_ACTIVE_CATALOG_VERSION >= 2 ? 'paper' : packageSelection.materialId,
            withFraming: SONIC_ACTIVE_CATALOG_VERSION >= 2 ? true : packageSelection.withFraming,
            deliveryOption:
              SONIC_ACTIVE_CATALOG_VERSION >= 2
                ? (digitalCheckout.fastTrack ? 'fast-track' : 'standard')
                : (packageSelection.formatType === 'digital' || packageSelection.formatType === 'phygital') && digitalCheckout.fastTrack ? 'fast-track' : 'standard',
            totalPrice: pricing.total,
            breakdown: {
              base: pricing.base,
              fastTrack: pricing.fastTrack,
              physical: pricing.physical,
              colorPrice: pricing.colorPrice,
            },
          };

          // 4. Add shipping address for physical/phygital orders.
          // v2 collects it after the customer chooses their interpretation,
          // because the print is of an artwork that does not exist yet.
          if (
            SONIC_ACTIVE_CATALOG_VERSION < 2 &&
            (packageSelection.formatType === 'physical' || packageSelection.formatType === 'phygital')
          ) {
            // Send both field name formats for compatibility with old/new backend validators
            const addr = physicalConfig.shippingAddress;
            requestData.shippingAddress = {
              fullName: addr.fullName,
              line1: addr.line1,
              line2: addr.line2,
              addressLine1: addr.line1, // Legacy field name
              addressLine2: addr.line2, // Legacy field name
              city: addr.city,
              state: addr.state,
              postalCode: addr.postalCode,
              country: addr.country,
              phone: addr.phone,
            };
          }

          // 5. Create Stripe checkout session (order will be created after payment)
          const checkoutData = {
            ...requestData,
            successUrl: `${window.location.origin}/sonic/success?session_id={CHECKOUT_SESSION_ID}`,
            // Back to the wizard, which reopens on the persisted checkout step.
            // (`/sonic/create` was never a route — Stripe's back button 404'd.)
            cancelUrl: `${window.location.origin}/sonic`,
            ...(state.digitalCheckout.discountCode
              ? { discountCode: state.digitalCheckout.discountCode }
              : {}),
          };

          const response = await requestService.createSonicCheckoutSession(checkoutData);

          if (response.success && response.data?.url) {
            // Redirect to Stripe Checkout
            window.location.href = response.data.url;
            // Keep isSubmitting true during redirect
            return true;
          } else {
            // The studio may have sold its last Express slot between the
            // availability probe and this submit. Clear the upgrade so the
            // customer can continue as standard without re-editing anything.
            if (response.error?.code === 'EXPRESS_UNAVAILABLE') {
              set((s) => ({
                digitalCheckout: { ...s.digitalCheckout, fastTrack: false },
                error: response.error?.message ?? 'Express Creation is no longer available.',
                isSubmitting: false,
              }));
              return false;
            }
            set({
              error: response.error?.message || 'Failed to create checkout session',
              isSubmitting: false,
            });
            return false;
          }
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to submit order',
            isSubmitting: false,
          });
          return false;
        }
      },

      // ===== PHASE 2 NAVIGATION =====

      setPhysicalStep: (step) => set({ physicalStep: step }),

      nextPhysicalStep: () => {
        const { physicalStep, canProceedPhysical } = get();
        if (canProceedPhysical() && physicalStep < 3) {
          set({ physicalStep: (physicalStep + 1) as SonicPhysicalStep, error: null });
        }
      },

      prevPhysicalStep: () => {
        const { physicalStep } = get();
        if (physicalStep > 1) {
          set({ physicalStep: (physicalStep - 1) as SonicPhysicalStep, error: null });
        }
      },

      canProceedPhysical: () => {
        const { physicalStep, physicalConfig, physicalCheckout } = get();

        switch (physicalStep) {
          case 1:
            // Config: require shipping address
            const addr = physicalConfig.shippingAddress;
            return (
              addr.fullName.trim().length > 0 &&
              addr.line1.trim().length > 0 &&
              addr.city.trim().length > 0 &&
              addr.postalCode.trim().length > 0 &&
              addr.country.trim().length > 0 &&
              addr.phone.trim().length > 0
            );

          case 2:
            // Checkout: must confirm production
            return physicalCheckout.productionConfirmed;

          default:
            return false;
        }
      },

      getPhysicalValidationErrors: () => {
        const { physicalStep, physicalConfig, physicalCheckout } = get();
        const errors: Record<string, string> = {};

        switch (physicalStep) {
          case 1: {
            const addr = physicalConfig.shippingAddress;
            if (!addr.fullName.trim()) errors['shipping.fullName'] = 'Full name is required';
            if (!addr.line1.trim()) errors['shipping.line1'] = 'Address is required';
            if (!addr.city.trim()) errors['shipping.city'] = 'City is required';
            if (!addr.postalCode.trim()) {
              errors['shipping.postalCode'] = 'Postal code is required';
            } else if (!/^[a-zA-Z0-9\s\-]{2,15}$/.test(addr.postalCode.trim())) {
              errors['shipping.postalCode'] = 'Please enter a valid postal code';
            }
            if (!addr.country.trim()) errors['shipping.country'] = 'Country is required';
            if (!addr.phone.trim()) {
              errors['shipping.phone'] = 'Phone number is required';
            } else {
              const digits = addr.phone.replace(/\D/g, '').length;
              if (digits < 7 || !/^\+?[\d\s\-().]{7,20}$/.test(addr.phone.trim())) {
                errors['shipping.phone'] = 'Please enter a valid phone number (at least 7 digits)';
              }
            }
            break;
          }

          case 2:
            if (!physicalCheckout.productionConfirmed) {
              errors.production = 'You must confirm to proceed';
            }
            break;
        }

        return errors;
      },

      // ===== PHASE 2 - INITIALIZE =====

      initPhysicalOrder: (requestId, artworkId = null) => {
        set({
          physicalStep: 1,
          physicalRequestId: requestId,
          physicalConfig: { ...initialPhysicalConfig, artworkId },
          physicalCheckout: { ...initialPhysicalCheckout },
          physicalOrder: { ...initialPhysicalOrder },
          error: null,
        });
      },

      // ===== PHASE 2 - STEP 1 (CONFIG) - Physical order flow =====

      setPhysicalEdition: (editionId) => {
        // Live catalogue, not v1 — see findSonicEdition. Searching the wrong
        // array here silently did nothing, so a v2 selection never applied.
        const edition = findSonicEdition(editionId);
        if (edition) {
          set({
            physicalConfig: {
              ...get().physicalConfig,
              editionId,
              // Sync legacy fields for backward compatibility
              sizeId: edition.size.toLowerCase() as SonicSizeId,
              materialId: edition.materialId as SonicMaterialId,
              withFraming: edition.isFramed,
            },
          });
        }
      },

      // Legacy setters (kept for backward compatibility)
      setPhysicalSize: (sizeId) =>
        set({ physicalConfig: { ...get().physicalConfig, sizeId } }),

      setPhysicalMaterial: (materialId) =>
        set({ physicalConfig: { ...get().physicalConfig, materialId } }),

      setPhysicalFraming: (withFraming) =>
        set({ physicalConfig: { ...get().physicalConfig, withFraming } }),

      setShippingField: (field, value) =>
        set({
          physicalConfig: {
            ...get().physicalConfig,
            shippingAddress: { ...get().physicalConfig.shippingAddress, [field]: value },
          },
        }),

      // ===== PHASE 2 - STEP 2 (CHECKOUT) =====

      setProductionConfirmed: (confirmed) =>
        set({ physicalCheckout: { ...get().physicalCheckout, productionConfirmed: confirmed } }),

      // ===== PHASE 2 - SUBMIT =====

      submitPhysicalOrder: async (artworkName?: string) => {
        const state = get();
        const { physicalRequestId, physicalConfig, getPhysicalPrice } = state;

        if (!physicalRequestId) {
          set({ error: 'No request ID specified', isSubmitting: false });
          return false;
        }

        set({ isSubmitting: true, error: null });

        try {
          const pricing = getPhysicalPrice();

          // Create Stripe checkout session - order is created after payment
          const response = await requestService.createPhysicalCheckoutSession(physicalRequestId, {
            editionId: physicalConfig.editionId,
            // Legacy fields for backward compatibility
            sizeId: physicalConfig.sizeId,
            materialId: physicalConfig.materialId,
            withFraming: physicalConfig.withFraming,
            artworkId: physicalConfig.artworkId,
            shippingAddress: physicalConfig.shippingAddress,
            totalPrice: pricing.total,
            artworkName: artworkName || 'Sonic Identity',
            successUrl: `${window.location.origin}/sonic/print/${physicalRequestId}?session_id={CHECKOUT_SESSION_ID}`,
            cancelUrl: `${window.location.origin}/sonic/print/${physicalRequestId}?cancelled=true`,
          });

          const result = response.data as
            | { requiresPayment?: boolean; url?: string; orderId?: string }
            | undefined;

          // Nothing to pay: the customer kept the print their bundle included,
          // so the order already exists. Go straight to confirmation instead of
          // bouncing through a $0 Stripe page.
          if (response.success && result?.requiresPayment === false && result.orderId) {
            set({
              physicalOrder: {
                ...get().physicalOrder,
                orderId: result.orderId,
                // Already paid, inside the bundle.
                status: 'paid' as const,
              },
              physicalStep: 3,
              isSubmitting: false,
              error: null,
            });
            return true;
          }

          if (response.success && result?.url) {
            // Redirect to Stripe checkout
            window.location.href = result.url;
            return true;
          } else {
            set({
              error: response.error?.message || 'Failed to create checkout session',
              isSubmitting: false,
            });
            return false;
          }
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Failed to submit order',
            isSubmitting: false,
          });
          return false;
        }
      },

      // ===== UI STATE =====

      setSubmitting: (submitting) => set({ isSubmitting: submitting }),
      setError: (error) => set({ error }),

      // ===== RESET =====

      resetDigitalWizard: () =>
        set({
          digitalStep: 1,
          digitalEssence: { ...initialDigitalEssence },
          packageSelection: { ...initialPackageSelection },
          digitalCheckout: { ...initialDigitalCheckout },
          isSubmitting: false,
          error: null,
        }),

      resetPhysicalWizard: () =>
        set({
          physicalStep: 1,
          physicalRequestId: null,
          physicalConfig: { ...initialPhysicalConfig },
          physicalCheckout: { ...initialPhysicalCheckout },
          physicalOrder: { ...initialPhysicalOrder },
          isSubmitting: false,
          error: null,
        }),

      // Combined reset for logout/cleanup
      resetWizard: () =>
        set({
          // Main wizard state
          digitalStep: 1,
          digitalEssence: { ...initialDigitalEssence },
          packageSelection: { ...initialPackageSelection },
          digitalCheckout: { ...initialDigitalCheckout },
          // Physical wizard state
          physicalStep: 1,
          physicalRequestId: null,
          physicalConfig: { ...initialPhysicalConfig },
          physicalCheckout: { ...initialPhysicalCheckout },
          physicalOrder: { ...initialPhysicalOrder },
          // UI state
          isSubmitting: false,
          error: null,
        }),
    }),
    {
      // v10: the bundle replaces the digital/physical/phygital split. Persisted
      // state from v9 can hold a formatType or material that v2 does not sell,
      // which would strand someone mid-wizard on options that no longer exist.
      name: 'soundbyte-sonic-v10',
      partialize: (state) => ({
        // Persist wizard progress (except File objects)
        digitalStep: state.digitalStep,
        digitalEssence: {
          prompt: state.digitalEssence.prompt,
          name: state.digitalEssence.name,
          meaning: state.digitalEssence.meaning,
          influenceTags: state.digitalEssence.influenceTags,
          email: state.digitalEssence.email,
          colorMode: state.digitalEssence.colorMode,
          colorPreferences: state.digitalEssence.colorPreferences,
          selectedColorHexes: state.digitalEssence.selectedColorHexes, // User-selected hex colors
          colorPaletteId: state.digitalEssence.colorPaletteId, // Legacy
          colorPalettePrice: state.digitalEssence.colorPalettePrice, // Legacy
          selectedColors: state.digitalEssence.selectedColors, // Legacy
          voiceUrl: state.digitalEssence.voiceUrl?.startsWith('http') ? state.digitalEssence.voiceUrl : null,
          voiceDuration: state.digitalEssence.voiceUrl?.startsWith('http') ? state.digitalEssence.voiceDuration : 0,
          voiceFile: null,
        },
        packageSelection: state.packageSelection,
        digitalCheckout: state.digitalCheckout,
        // Don't persist physical data
      }),
      // Merge persisted state with initial state to handle new fields
      merge: (persistedState: any, currentState) => {
        const digitalEssence: DigitalEssenceData = {
          ...initialDigitalEssence,
          ...persistedState?.digitalEssence,
        };
        // Reopen on the checkout only if it can still be paid. A voice
        // recorded but not yet uploaded is a blob URL, which partialize drops,
        // so a reload mid-checkout must land on the recorder rather than on a
        // checkout with nothing to pay for. (The wizard also went from three
        // steps to two; an old step 3 maps onto the new checkout.)
        const persistedStep = Number(persistedState?.digitalStep) || 1;
        const digitalStep = (
          persistedStep >= LAST_DIGITAL_STEP && isEssenceComplete(digitalEssence)
            ? LAST_DIGITAL_STEP
            : 1
        ) as SonicDigitalStep;
        return {
          ...currentState,
          ...persistedState,
          digitalStep,
          digitalEssence,
        };
      },
    }
  )
);

// ===== SELECTOR HOOKS =====

// Main wizard - primitive selectors (no object creation)
export const useSonicDigitalStep = () => useSonicStore((state) => state.digitalStep);
export const useSonicDigitalEssence = () => useSonicStore((state) => state.digitalEssence);
export const useSonicDigitalCheckout = () => useSonicStore((state) => state.digitalCheckout);

// Package price - use shallow comparison for computed object
export const useSonicPackagePrice = () =>
  useSonicStore(
    useShallow((state) => {
      const { formatType: formatTypeId, sizeId, materialId } = state.packageSelection;

      // Color pricing: flat $149 for colorful mode, $0 for black & white
      const colorPrice = state.digitalEssence.colorMode === 'colorful' ? SONIC_COLOR_PRICING.colorfulPrice : 0;

      // v2 sells one product. There is no format, size or material to price at
      // checkout — the print is of an artwork that does not exist yet, so those
      // choices happen after the customer selects their favourite.
      if (SONIC_ACTIVE_CATALOG_VERSION >= 2) {
        const base = SONIC_BUNDLE_V2.price;
        const express = state.digitalCheckout.fastTrack ? SONIC_DELIVERY_V2.express.price : 0;
        return { base, fastTrack: express, physical: 0, colorPrice: 0, total: base + express };
      }

      // Get edition price for physical options
      const getEditionPrice = () => {
        const sizeLabel = SONIC_SIZES.find(s => s.id === sizeId)?.label;
        const edition = SONIC_PHYSICAL_EDITIONS.find(
          e => e.size === sizeLabel && e.materialId === materialId
        );
        return edition?.price || SONIC_PHYSICAL_EDITIONS[0].price;
      };

      // Digital only: base = $99, no physical
      if (formatTypeId === 'digital') {
        const base = DIGITAL_BASE_PRICE;
        const fastTrack = state.digitalCheckout.fastTrack ? FAST_TRACK_PRICE : 0;
        return { base, fastTrack, physical: 0, colorPrice, total: base + fastTrack + colorPrice };
      }

      // Physical only: base = edition price, no fast-track
      if (formatTypeId === 'physical') {
        const base = getEditionPrice();
        return { base, fastTrack: 0, physical: 0, colorPrice, total: base + colorPrice };
      }

      // Phygital: base = $99 digital, physical = edition price
      if (formatTypeId === 'phygital') {
        const base = DIGITAL_BASE_PRICE;
        const physical = getEditionPrice();
        const fastTrack = state.digitalCheckout.fastTrack ? FAST_TRACK_PRICE : 0;
        return { base, fastTrack, physical, colorPrice, total: base + fastTrack + physical + colorPrice };
      }

      // Fallback
      return { base: DIGITAL_BASE_PRICE, fastTrack: 0, physical: 0, colorPrice, total: DIGITAL_BASE_PRICE + colorPrice };
    })
  );

// Phase 2: Physical - primitive selectors
export const useSonicPhysicalStep = () => useSonicStore((state) => state.physicalStep);
export const useSonicPhysicalConfig = () => useSonicStore((state) => state.physicalConfig);
export const useSonicPhysicalCheckout = () => useSonicStore((state) => state.physicalCheckout);
export const useSonicPhysicalOrder = () => useSonicStore((state) => state.physicalOrder);

// Physical config for price calculation - use shallow comparison
export const useSonicPhysicalPriceConfig = () =>
  useSonicStore(
    useShallow((state) => ({
      editionId: state.physicalConfig.editionId,
      sizeId: state.physicalConfig.sizeId,
      materialId: state.physicalConfig.materialId,
      withFraming: state.physicalConfig.withFraming,
    }))
  );

// Physical price - DEPRECATED, use useSonicPhysicalPriceConfig with useMemo instead
// Keeping for backward compatibility but callers should memoize themselves
export const useSonicPhysicalPrice = () => {
  const config = useSonicPhysicalPriceConfig();
  // This is intentionally computed directly - callers should use useMemo
  return calculateSonicPrice({
    editionId: config.editionId,
    sizeId: config.sizeId,
    materialId: config.materialId,
    withFraming: config.withFraming,
    deliveryId: 'standard',
  });
};

// Legacy exports for backward compatibility with existing code
export const useSonicStep = () => useSonicStore((state) => state.digitalStep);
export const useSonicEssence = () => useSonicStore((state) => state.digitalEssence);
export const useSonicPackage = () => useSonicStore((state) => state.physicalConfig);

// Legacy delivery - use shallow comparison
export const useSonicDelivery = () =>
  useSonicStore(
    useShallow((state) => ({
      deliveryId: state.digitalCheckout.fastTrack ? 'fast-track' : 'standard',
      confirmationChecked: state.digitalCheckout.termsAccepted,
    }))
  );

// Legacy checkout - use shallow comparison
export const useSonicCheckout = () =>
  useSonicStore(
    useShallow((state) => ({
      email: state.digitalEssence.email,
      fullName: state.physicalConfig.shippingAddress.fullName,
      phone: state.physicalConfig.shippingAddress.phone,
      address: state.physicalConfig.shippingAddress,
      paymentCompleted: false,
    }))
  );

// Legacy pricing - use shallow comparison
export const useSonicPricing = () =>
  useSonicStore(
    useShallow((state) => {
      const base = DIGITAL_BASE_PRICE;
      const fastTrack = state.digitalCheckout.fastTrack ? FAST_TRACK_PRICE : 0;
      return { base, fastTrack, total: base + fastTrack };
    })
  );
